import { DOMParser } from 'xmldom';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './cscb.scss';
import navigationService from '../../../../services/NavigationService';
import responseService from '../../../../services/ResponseService';
import { t } from '../../../../services/i18nService';
import questionSelectionReader from '../../../../store/readHelpers/QuestionSelectionReader';
import stylesQTI from '../../../question/QTIAssessmentItem.scss';
import { getItemBodyAsHTML, getQuestionTextFromItemBody } from '../../../../qti/parser/question/itemBody';
import stylesStimulus from '../../../stimulusDisplay/StimulusDisplay.scss';
import { reportError } from './../../../../services/ErrorReporting';

function CscbAccordion(props) {
    const {
        number,
        questionPairs,
        sectionId,
        currQuestion,
        areAllQuestionsPrepared,
    } = props;
    const [open, setOpen] = useState(true);
    const { isSelected, stimulusQuestion, contentRevisionId } = currQuestion;
    let questionContent = null;

    if (stimulusQuestion) {
        try {
            const itemBodyElement = new DOMParser().parseFromString(stimulusQuestion?.itemBody, 'text/xml').firstChild;
            if (itemBodyElement) {
                questionContent = getItemBodyAsHTML(stimulusQuestion?.contentRevisionId, itemBodyElement, stimulusQuestion?.defaultLanguage);
            }
        } catch (error) {
            reportError(error);
        }
    }

    if (!questionContent && currQuestion && currQuestion.itemBody) {
        questionContent = getQuestionTextFromItemBody(currQuestion.itemBody);
    }

    const handleToggle = () => setOpen(prev => !prev);
    const classNames = [stylesQTI.QTIAssessmentItem, 'preRender', styles.stimulusContent];
    const contentClasses = [stylesStimulus.sectionStimulus];

    const onQuestionClick = (event) => {
        event.preventDefault();
        if (questionPairs?.length > 0) {
            try {
                // For "Review answer" (isSelected === YES), we should navigate directly to the question
                // For "Change to this option" and "Answer this option", we need to handle selection first and then navigate
                if (isSelected !== questionSelectionReader.selectionType.YES) {
                    responseService.handleContentBlockSelection(questionPairs, contentRevisionId);
                }
                navigationService.navigateToQuestion(contentRevisionId, sectionId, true, true);
            } catch (error) {
                reportError(error);
            }
        } else {
            reportError('No questions found for the selected stimulus in CscbAccordion');
        }
    };

    const getSelectionStatus = () => {
        return isSelected === undefined ? questionSelectionReader.selectionType.UNDECIDED : isSelected;
    };

    return (
        <div className={styles.accordion}>
            <div
                key={`accordion-header-${number}`}
                className={`${styles.accordionHeader} ${!open ? styles.accordionClosed : ''}`}
            >
                <div className={styles.titleContainer}>
                    <button
                        aria-expanded={open}
                        onClick={handleToggle}
                    >
                        {open ? <i className={`fa fa-chevron-down ${styles.accordionActionIcon}`} aria-hidden="true" /> : (
                            <i className={`fa fa-chevron-right ${styles.accordionActionIcon}`} aria-hidden="true" />)}
                    </button>
                    <h2 className={styles.title}>
                        {t('interactions.cscb.option')} {number}
                    </h2>
                </div>

                <div>
                    {isSelected === questionSelectionReader.selectionType.YES && (
                        <p className={`${styles.selectedSectionBtn}`}>
                            <i className="fa fa-check" aria-hidden="true" alt="" /> {t('interactions.cscb.questionSelected')}
                        </p>
                    )}
                    {isSelected === questionSelectionReader.selectionType.NO && (
                        <p className={`${styles.unSelectedSectionBtn}`}>
                            <i className="fa fa-exclamation-triangle" style={{ color: '#F2C602' }} aria-hidden="true" alt="" />
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'interactions.cscb.questionNotSelected',
                                        {
                                            currentOption: number,
                                            otherOption: number === 1 ? 2 : 1,
                                        }
                                    ),
                                }}
                            />
                        </p>
                    )}
                </div>
                <button
                    className={`btn ${styles.answerButton}`}
                    type="button"
                    onClick={onQuestionClick}
                    disabled={!areAllQuestionsPrepared}
                    alt=""
                >
                    {areAllQuestionsPrepared ?
                        t(`interactions.cscb.answerThisOption.${getSelectionStatus()}`) :
                        t('interactions.cscb.preparingQuestions')
                    }
                    <i className="fa fa-arrow-right" aria-hidden="true" />
                </button>
            </div>
            {(open && sectionId && questionContent) && (
                <div className={`${contentClasses} ${styles.stimulus}`}>
                    <div className={stylesQTI.QTIAssessmentItemWrapper}>
                        <div className={classNames.join(' ')}>
                            <div dangerouslySetInnerHTML={{ __html: questionContent }} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

CscbAccordion.propTypes = {
    number: PropTypes.number.isRequired,
    questionPairs: PropTypes.arrayOf(PropTypes.object).isRequired,
    sectionId: PropTypes.number.isRequired,
    currQuestion: PropTypes.any,
    areAllQuestionsPrepared: PropTypes.bool,
};

export default CscbAccordion;
